import { useMemo } from 'react';
import { getHours } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Detection } from '@packages/shared-types';

import { LoadingLogo } from '@/components/shared/LoadingLogo';

interface ActivityTimelineChartProps {
  detections: Detection[];
  isLoading?: boolean;
}

export function ActivityTimelineChart({
  detections,
  isLoading,
}: ActivityTimelineChartProps): JSX.Element {
  const chartData = useMemo(() => {
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentDetections = detections.filter(
      (d) => new Date(d.detectedAt) > twentyFourHoursAgo,
    );

    // Group by hour
    const groupedByHour = recentDetections.reduce(
      (acc, detection) => {
        const hour = getHours(new Date(detection.detectedAt));
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      },
      {} as Record<number, number>,
    );

    // Create array for all 24 hours
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`,
      count: groupedByHour[i] || 0,
    }));

    return hours;
  }, [detections]);

  if (isLoading) {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-3">
        <LoadingLogo size="sm" />
        <span className="text-sm text-muted-foreground">Loading chart...</span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.8} />
            <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="label"
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
          interval={2}
        />
        <YAxis
          className="text-xs"
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.5rem',
            color: 'hsl(var(--card-foreground))',
          }}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="hsl(var(--accent))"
          fill="url(#colorCount)"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
